import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { MessageService } from 'primeng/api';
import { LocalStorageUtils } from '../utils/user.utils';
import { ROUTES, STORAGE_KEYS } from '../constants/app.constants';
import { eKeyLocalStorage } from '../constants/options-system';

@Injectable({
  providedIn: 'root'
})
export class SessionExpiryService {
  // Cerrar la sesión requiere el mismo SupabaseClient que usa el resto de la app
  // (ver SupabaseService). Se registra en tiempo de ejecución para evitar crear
  // una segunda instancia de GoTrueClient sobre el mismo storage key, lo cual
  // provocaba que el refresh automático de token fallara de forma intermitente.
  private signOutHandler: (() => Promise<void>) | null = null;
  private handlingSessionExpiry = false;

  // Sesión activa (idle timeout): si el usuario no interactúa en este lapso, se cierra la sesión.
  private readonly IDLE_TIMEOUT_MS = 3 * 60 * 60 * 1000; // 3 horas
  // Sesión absoluta: fuerza re-login pasado este tiempo aunque el usuario esté activo.
  private readonly ABSOLUTE_TIMEOUT_MS = 8 * 60 * 60 * 1000; // 8 horas
  private readonly CHECK_INTERVAL_MS = 60 * 1000;
  private readonly ACTIVITY_WRITE_THROTTLE_MS = 30 * 1000;
  private readonly SESSION_STARTED_KEY = 'ipay_admin_session_started_at';
  private readonly LAST_ACTIVITY_KEY = 'ipay_admin_session_last_activity_at';
  private readonly ACTIVITY_EVENTS = ['click', 'keydown', 'mousemove', 'scroll', 'touchstart'];

  private activityCheckIntervalId: ReturnType<typeof setInterval> | null = null;
  private listenersAttached = false;
  private lastActivityWriteAt = 0;
  private readonly boundRegisterActivity = () => this.registerActivity();

  constructor(
    private router: Router,
    private messageService: MessageService
  ) { }

  /**
   * Permite que SupabaseService exponga cómo cerrar sesión usando el único
   * SupabaseClient de la app, sin que este servicio necesite depender de él
   * (evitaría una dependencia circular).
   */
  registerSignOutHandler(handler: () => Promise<void>): void {
    this.signOutHandler = handler;
  }

  /**
   * Inicia o retoma el seguimiento de actividad/duración de la sesión.
   * Se llama en cada arranque de la app (login, recarga de página, o al volver
   * a una pestaña que el navegador descargó de memoria), no solo al iniciar sesión.
   * Por eso ambas marcas se preservan si ya existían: si no lo hiciéramos, cada
   * recarga reiniciaría el reloj de inactividad y el idle timeout nunca se cumpliría.
   */
  ensureSessionTracking(): void {
    const now = Date.now();
    if (!localStorage.getItem(this.SESSION_STARTED_KEY)) {
      localStorage.setItem(this.SESSION_STARTED_KEY, String(now));
    }
    if (!localStorage.getItem(this.LAST_ACTIVITY_KEY)) {
      localStorage.setItem(this.LAST_ACTIVITY_KEY, String(now));
    }

    this.attachActivityListeners();
    this.startActivityCheckInterval();
  }

  /**
   * Arranca el reloj de sesión desde cero. A diferencia de `ensureSessionTracking()`,
   * que preserva marcas existentes al retomar una sesión (recarga, reapertura de
   * pestaña), este método se usa exclusivamente cuando el usuario acaba de
   * autenticarse (login real). Evita que un login nuevo en el mismo navegador
   * herede el `SESSION_STARTED_KEY`/`LAST_ACTIVITY_KEY` de una sesión previa que
   * no cerró sesión explícitamente (p. ej. cerró la pestaña sin hacer logout).
   */
  startNewSession(): void {
    const now = Date.now();
    localStorage.setItem(this.SESSION_STARTED_KEY, String(now));
    localStorage.setItem(this.LAST_ACTIVITY_KEY, String(now));

    this.attachActivityListeners();
    this.startActivityCheckInterval();
  }

  /**
   * Detiene el seguimiento y limpia las marcas de sesión. Se debe llamar en todo logout.
   */
  stopSessionTracking(): void {
    this.detachActivityListeners();

    if (this.activityCheckIntervalId) {
      clearInterval(this.activityCheckIntervalId);
      this.activityCheckIntervalId = null;
    }

    localStorage.removeItem(this.SESSION_STARTED_KEY);
    localStorage.removeItem(this.LAST_ACTIVITY_KEY);
  }

  private attachActivityListeners(): void {
    if (this.listenersAttached) {
      return;
    }

    this.ACTIVITY_EVENTS.forEach((eventName) => {
      document.addEventListener(eventName, this.boundRegisterActivity, { passive: true });
    });
    this.listenersAttached = true;
  }

  private detachActivityListeners(): void {
    if (!this.listenersAttached) {
      return;
    }

    this.ACTIVITY_EVENTS.forEach((eventName) => {
      document.removeEventListener(eventName, this.boundRegisterActivity);
    });
    this.listenersAttached = false;
  }

  private registerActivity(): void {
    const now = Date.now();
    if (now - this.lastActivityWriteAt < this.ACTIVITY_WRITE_THROTTLE_MS) {
      return;
    }

    this.lastActivityWriteAt = now;
    localStorage.setItem(this.LAST_ACTIVITY_KEY, String(now));
  }

  private startActivityCheckInterval(): void {
    if (this.activityCheckIntervalId) {
      return;
    }

    this.activityCheckIntervalId = setInterval(() => this.checkSessionLimits(), this.CHECK_INTERVAL_MS);
  }

  private checkSessionLimits(): void {
    if (this.handlingSessionExpiry) {
      return;
    }

    const reason = this.getExceededLimitReason();
    if (reason) {
      this.handleSessionExpired(reason);
    }
  }

  /**
   * Evalúa de forma síncrona si la sesión ya superó el idle timeout (3h sin
   * actividad) o el tope absoluto (8h desde el login), sin depender de que el
   * intervalo periódico (`CHECK_INTERVAL_MS`) ya haya corrido. La usa el route
   * guard para no dejar pasar una navegación cuando la sesión ya venció pero el
   * intervalo aún no llegó a detectarlo (hasta 60s de margen).
   */
  isSessionWithinLimits(): boolean {
    return this.getExceededLimitReason() === null;
  }

  private getExceededLimitReason(): string | null {
    const startedAt = Number(localStorage.getItem(this.SESSION_STARTED_KEY));
    const lastActivityAt = Number(localStorage.getItem(this.LAST_ACTIVITY_KEY));

    if (!startedAt || !lastActivityAt) {
      return null;
    }

    const now = Date.now();

    if (now - lastActivityAt >= this.IDLE_TIMEOUT_MS) {
      return 'Tu sesión se cerró por inactividad. Vuelve a iniciar sesión.';
    }

    if (now - startedAt >= this.ABSOLUTE_TIMEOUT_MS) {
      return 'Tu sesión alcanzó el tiempo máximo de duración. Vuelve a iniciar sesión.';
    }

    return null;
  }

  async handleSessionExpired(reason?: string): Promise<void> {
    if (this.handlingSessionExpiry) {
      return;
    }

    this.handlingSessionExpiry = true;

    try {
      this.messageService.clear();
      this.messageService.add({
        key: 'toat-message',
        severity: 'error',
        summary: 'Sesión expirada',
        detail: reason || 'Tu sesión expiró. Vuelve a iniciar sesión.',
        life: 5000
      });

      if (this.signOutHandler) {
        await this.signOutHandler();
      }
    } catch (error) {
      console.error('Error al cerrar la sesión expirada:', error);
    } finally {
      this.stopSessionTracking();
      LocalStorageUtils.removeUserData(true);
      LocalStorageUtils.removeUserData(false);
      localStorage.removeItem(STORAGE_KEYS.USER_DATA);
      localStorage.removeItem(STORAGE_KEYS.COMPANY_USER_DATA);
      localStorage.removeItem(eKeyLocalStorage.BusinessSelected);
      this.redirectToLogin();
      this.handlingSessionExpiry = false;
    }
  }

  private redirectToLogin(): void {
    const currentUrl = this.router.url;

    // replaceUrl: evita apilar entradas de historial hacia login (ver AuthService.redirectToLogin).
    if (currentUrl.includes('/dashboard/company') || currentUrl.includes('/auth/company')) {
      this.router.navigate([ROUTES.AUTH.COMPANY.LOGIN], { replaceUrl: true });
      return;
    }

    if (currentUrl.includes('/dashboard/user') || currentUrl.includes('/auth/user')) {
      this.router.navigate([ROUTES.AUTH.USER.LOGIN], { replaceUrl: true });
      return;
    }

    this.router.navigate([ROUTES.AUTH.COMPANY.LOGIN], { replaceUrl: true });
  }
}
